<!-- src/App.vue -->
<template>
  <div id="app-container">
    <!-- Staff-only operational banners. These are INTERNAL CRM machinery and must
         never render on a customer-facing surface: a customer was shown the old
         cache-recovery banner on the portal activation page (2026-07-21). Hidden on
         public/consumer routes and for customer accounts. (The cache-recovery banner
         itself was retired 2026-09-05; tabs refresh in the background instead.) -->
    <template v-if="showStaffBanners">
      <ImpersonationBanner />
    </template>
    <router-view />

    <!-- Store modals mount at the end of <body> WHEN THEY OPEN. Task-entry dialogs (a modal task's
         "Open details", multi-line text, multiple dates) teleport to <body> at the same z-modal layer,
         so a store modal they open (e.g. Send Proposal's Preview & Send) must come after them in the
         document to land on top. Rendered here inside #app-container it always lost that tie and opened
         underneath (bug XRYBBajbDED6EQAMaT3B). The v-if is on the Teleport so its anchor is created at
         open time, after every dialog already open. -->
    <Teleport v-if="modalStore.isOpen" to="body">
      <!-- Wrapped Modals (for simple content) -->
      <GenericModal
        v-if="!modalStore.isStandalone && currentGenericModal"
        :title="modalStore.modalProps?.title || 'Dialog'"
        :max-width="modalStore.modalProps?.maxWidth || 'max-w-5xl'"
        :max-height="modalStore.modalProps?.maxHeight || '80vh'"
        @close="modalStore.closeModal()"
      >
        <component
          :is="currentGenericModal"
          v-bind="modalStore.modalProps"
          @close="modalStore.closeModal()"
          @save="handleGenericModalSave"
        />
      </GenericModal>

      <!-- Standalone Modals (for complex, full-screen modals) -->
      <component
        v-else-if="modalStore.isStandalone && currentStandaloneModal"
        :is="currentStandaloneModal"
        v-bind="modalStore.modalProps"
        @close="modalStore.closeModal()"
      />
    </Teleport>

    <GlobalConfirmModal />
    <HighFrictionConfirmModal />
    <GlobalLoadingSpinner />
    <IdleTimeoutWarning />
    <ActiveCallNavigationModal />

    <!-- Global site-wide components (dynamically loaded from active modules):
         Call Center panel, internal Site Chat, Walkie. These are INTERNAL STAFF
         TOOLS and must never be reachable by a customer. See showInternalOverlays
         — gated positively (staff only) rather than by a list of pages to hide on. -->
    <template v-if="showInternalOverlays">
      <component v-for="gc in visibleGlobalComponents" :key="gc.name" :is="gc.component" />
      <TeamVoicePanel :model="teamVoice" />
    </template>

    <!-- Announcement Overlay -->
    <Teleport to="body">
      <Transition name="announcement">
        <div
          v-if="currentAnnouncement"
          class="pointer-events-none fixed inset-0 z-[99999] flex items-start justify-center pt-20"
        >
          <div
            class="animate-bounce-in pointer-events-auto mx-4 w-full max-w-lg rounded-2xl border border-blue-400/30 bg-blue-600/85 text-white shadow-2xl backdrop-blur-sm"
          >
            <div class="p-5">
              <div class="flex items-start gap-3">
                <div class="min-w-0 flex-1">
                  <p class="text-base italic leading-relaxed text-white">
                    {{ currentAnnouncement.message }}
                  </p>
                  <div class="mt-3 text-right text-sm text-blue-100">
                    From: {{ currentAnnouncement.senderName }}
                  </div>
                  <div class="mt-0.5 text-right text-xs text-blue-200/70">
                    {{ formatAnnouncementTime(currentAnnouncement.createdAt) }}
                  </div>
                </div>
                <button
                  @click="dismissAnnouncement"
                  class="-mr-1 -mt-1 shrink-0 rounded-full p-1.5 transition hover:bg-white/20"
                >
                  <X class="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
  <!-- Walkie-Talkie Remote Audio (persists across navigation) -->
  <audio ref="walkieRemoteAudio" autoplay playsinline></audio>
  <!-- Group Walkie: invite, call panel and its audio (persists across navigation) -->
  <GroupWalkiePanel v-if="userStore.isStaff && frontendFlags.isModuleEnabled('chat')" />
</template>

<script setup>
import { collection, query, orderBy, limit } from 'firebase/firestore'
import { onSnapshot } from '@/services/sessionListeners'
import { playChatNotificationTone } from '@/utils/audioTones'
import { ref, onMounted, onUnmounted, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { firebaseApp, analytics, functions, db } from '@/services/firebaseconfig'
import { jsonAuthHeaders } from '@/utils/firebase/authHeader'
import { logEvent } from 'firebase/analytics'
import { handleUserRoleRedirect, homePathForRoles } from '@/constants/roleRedirect'
import { shouldUseMobileSidecar } from '@/composables/useDeviceDetect'
import GlobalConfirmModal from '@/components/Common/GlobalConfirmModal.vue'
import HighFrictionConfirmModal from '@/components/Common/HighFrictionConfirmModal.vue'
import GlobalLoadingSpinner from '@/components/Common/GlobalLoadingSpinner.vue'
import GenericModal from '@/components/Common/GenericModal.vue'
import IdleTimeoutWarning from '@/components/Common/IdleTimeoutWarning.vue'
import ActiveCallNavigationModal from '@/components/Communications/ActiveCallNavigationModal.vue'
import TeamVoicePanel from '@/components/Communications/TeamVoicePanel.vue'
import ImpersonationBanner from '@/components/Layout/ImpersonationBanner.vue'
import { X } from 'lucide-vue-next'
import { useToast } from '@/composables/useToast'
import { useModalStore } from '@/stores/modalStore'
import { MODAL_COMPONENT_REGISTRY } from '@/config/modalComponentRegistry'
import { normalizeTimestamp } from '@/utils/formatters'

// Pinia Stores — Core (always loaded)
import { useUserStore } from '@/stores/userStore'
import { useUsersStore } from '@/stores/usersStore'
import { useWorkflowStagesStore } from '@/stores/workflowStagesStore'
import { useCatalogCompatibilityStore } from '@/stores/catalogCompatibilityStore'
import { useWorkflowTasksStore } from '@/stores/workflowTasksStore'
import { useWorkflowProjectTypesStore } from '@/stores/workflowProjectTypesStore'
import { useDataStore } from '@/stores/dataStore'
import { useLeadStore } from '@/stores/leadStore'
import { useAlertPreferencesStore } from '@/stores/alertPreferencesStore'
import { usePermissionsStore } from '@/stores/permissionsStore'

// Module-owned stores kept for App-level watches (walkie audio, chat/walkie init, voice init)
import { useWalkieStore } from '@/stores/walkieStore'
import { useTeamVoiceStore } from '@/stores/teamVoiceStore'
import { useUserChatStore } from '@/stores/userChatStore'
import { useGroupChatStore } from '@/stores/groupChatStore'
import { useGroupWalkieStore } from '@/stores/groupWalkieStore'
import GroupWalkiePanel from '@/components/Communications/GroupWalkiePanel.vue'
import { useCustomerChatStore } from '@/stores/customerChatStore'

// Module system — feature flags + module loader
import { useFrontendFlagsStore } from '@/stores/frontendFlagsStore'
import { useConsumerSiteConfigStore } from '@/stores/consumerSiteConfigStore'
import { useBlogConfigStore } from '@/stores/blogConfigStore'
import { useAuthConfigStore } from '@/stores/authConfigStore'
import { useTestCustomerMarkersStore } from '@/stores/testCustomerMarkersStore'
import { useIdleTimer } from '@/composables/useIdleTimer'
import {
  initializeModules,
  cleanupModules,
  globalComponents as moduleGlobalComponents,
} from '@/modules/moduleLoader'
import { voiceCallService } from '@/services/VoiceCallService'
import { localVoiceOwnership } from '@/services/localVoiceOwnership'
import { hasActiveCallElsewhere, onExternalOwnerChange } from '@/services/activeCallTabCoordinator'
import {
  isSessionEnding,
  planSessionTransition,
  reloadAfterSessionChange,
} from '@/services/sessionEnd'
import { useWebPush } from '@/composables/useWebPush'
import { unlockAudio } from '@/utils/audioTones'
import { eventBus } from '@/utils/eventBus'

useToast() // Initialize eventBus listener
const userStore = useUserStore()
const usersStore = useUsersStore()
const modalStore = useModalStore()
const workflowStagesStore = useWorkflowStagesStore()
const workflowTasksStore = useWorkflowTasksStore()
const workflowProjectTypesStore = useWorkflowProjectTypesStore()
const testCustomerMarkersStore = useTestCustomerMarkersStore()
const catalogCompatibilityStore = useCatalogCompatibilityStore()
const dataStore = useDataStore()
const leadStore = useLeadStore()
const alertPreferencesStore = useAlertPreferencesStore()
const router = useRouter()
const route = useRoute()
const permissionsStore = usePermissionsStore()
const userChatStore = useUserChatStore()
const groupChatStore = useGroupChatStore()
const groupWalkieStore = useGroupWalkieStore()
const customerChatStore = useCustomerChatStore()
const walkieRemoteAudio = ref(null)

// Staff-only operational banners (impersonation + Firestore cache recovery).
// Suppressed on public/consumer routes and for customer accounts — these speak
// in internal terms ("CRM cache version", "close every other GSE tab") and are
// meaningless and alarming to a customer. `route.meta.public` covers the
// consumer site + activation/sign-in pages; the role check covers a signed-in
// customer inside /portal, which is NOT flagged public.
const showStaffBanners = computed(
  () => !route.meta?.public && !(userStore.isCustomer || userStore.linkedCustomerId)
)

// Module system — subscribe to feature flags immediately (no auth needed)
const frontendFlags = useFrontendFlagsStore()
frontendFlags.subscribe()

// Consumer site config — subscribe immediately (public doc, no auth needed)
const consumerSiteConfig = useConsumerSiteConfigStore()
consumerSiteConfig.subscribe()

// Blog config — subscribe immediately (public doc, no auth needed)
const blogConfig = useBlogConfigStore()
blogConfig.subscribe()

// Auth config — subscribe immediately so the idle timer can read thresholds
// as soon as a user becomes present.
const authConfig = useAuthConfigStore()
authConfig.subscribe()

// Idle timer — singleton. Internally no-ops until user is signed in AND config
// is enabled. Instantiating here gives App.vue ownership of lifecycle.
useIdleTimer()

// Expose globalComponents from module loader for template rendering
const activeGlobalComponents = moduleGlobalComponents

// Check if we're on a kiosk/public/consumer page (hide global widgets like Call Center)
const isKioskMode = computed(() => {
  // Hide for kiosk
  if (['KioskCreatePage', 'Kiosk'].includes(route.name)) return true
  // Hide for all consumer site pages (public routes with ConsumerLayout)
  const consumerRoutes = [
    'ConsumerHome',
    'ConsumerAboutUs',
    'ConsumerContact',
    'ConsumerTestimonials',
    'ConsumerFAQs',
    'ConsumerHowItWorks',
    'ConsumerBlog',
    'BlogDetail',
    'SavingsCalculator',
    'ScheduleCall',
  ]
  if (consumerRoutes.includes(route.name)) return true
  // Also hide on root path (homepage)
  if (route.path === '/') return true
  return false
})

// Whether to mount the INTERNAL global overlays (Call Center panel, internal
// Site Chat, Walkie). These are staff tools; a customer must never see them.
//
// ⚠ GATED POSITIVELY ON PURPOSE (2026-07-21). This used to be
// `userStore.user && isInitialized && !isKioskMode`, where isKioskMode checked a
// hardcoded ALLOWLIST of consumer route names. That is fail-OPEN: a logged-in
// CUSTOMER satisfies `userStore.user`, and no /portal route was ever added to the
// list — so the Call Center panel and internal Site Chat rendered on the customer
// portal, sign-in, activation and unsubscribe pages. Every new public page leaked
// by default, because the list had to be edited by someone who knew it existed.
//
// The rule is now "show ONLY to a signed-in internal user, never on a public
// route" — so a new page is private unless it explicitly opts in, and forgetting
// something fails closed.
const showInternalOverlays = computed(() => {
  if (!userStore.user || !userStore.isInitialized) return false // not signed in
  if (userStore.isCustomer || userStore.linkedCustomerId) return false // customer account
  if (route.meta?.public) return false // consumer / public route
  if (isKioskMode.value) return false // kiosk + legacy consumer-route list
  return true
})

// On the mobile sidecar, suppress the floating desktop comms widgets (Call
// Center panel + chat launcher). WalkiePanel is ALSO hidden on /m — its
// presentational role is replaced by the full-screen MobileCallScreen (mounted in
// MobileLayout). walkieStore.initialize() runs here in App.vue (not the panel) and
// owns the WebRTC audio, so hiding the panel keeps walkie fully working on mobile.
const MOBILE_HIDDEN_OVERLAYS = ['CallPanel', 'SiteChat', 'WalkiePanel']
const isMobileRoute = computed(() => route.meta?.mobile === true)
const visibleGlobalComponents = computed(() => {
  const list = activeGlobalComponents.value || []
  if (!isMobileRoute.value) return list
  return list.filter(gc => !MOBILE_HIDDEN_OVERLAYS.includes(gc.name))
})

// Track if we're currently logging out to prevent race conditions
let isLoggingOut = false

// Track active data fetch so we can abort on logout
let dataFetchAbortController = null

// The ONE sink for a walkie call's remote voice (only when chat module active). The store keeps no audio element of
// its own: a second autoplaying sink played the voice twice (docs/bugs/walkie_call_lifecycle_BUG.md). A call's end
// (the stream back to null) releases it.
const walkieStore = useWalkieStore()
const teamVoice = useTeamVoiceStore()
voiceCallService.setAudioOwnership(localVoiceOwnership)
walkieStore.setAudioOwnership(localVoiceOwnership)
watch(
  () => {
    const allowed =
      showInternalOverlays.value &&
      userStore.isStaff &&
      !userStore.isCustomer &&
      !userStore.isReadOnly &&
      !userStore.isImpersonating &&
      userStore.user?.status?.accountStatus === 'active' &&
      permissionsStore.isInitialized &&
      permissionsStore.canAccessPage('MobileHome', userStore.roles, userStore.isSuperAdmin) &&
      frontendFlags.isModuleEnabled('chat') &&
      frontendFlags.isModuleEnabled('communications') &&
      frontendFlags.isFlagOn('nativeStaffTeamVoice')
    return {
      uid: userStore.user?.uid || null,
      allowed: !!allowed,
      ptt: !!allowed && frontendFlags.isFlagOn('nativeStaffPushToTalk'),
      scopeKey: JSON.stringify([
        userStore.roles,
        userStore.user?.organization || null,
        permissionsStore.permissions.pages?.MobileHome || null,
      ]),
    }
  },
  value => {
    teamVoice.configure(value)
    walkieStore.setTeamVoiceAdapter(
      value.allowed ? uid => teamVoice.open({ kind: 'direct', peerId: uid }, 'open') : null
    )
  },
  { immediate: true }
)
watch(
  () => (frontendFlags.isModuleEnabled('chat') ? walkieStore.remoteStream : null),
  stream => {
    if (!walkieRemoteAudio.value) return
    walkieRemoteAudio.value.srcObject = stream || null
    if (stream) console.log('[App] Walkie remote audio connected')
  },
  { immediate: true }
)

// Initialize presence when user logs in
// Announcement listener state
let announcementUnsubscribe = null
const currentAnnouncement = ref(null)

function dismissAnnouncement() {
  currentAnnouncement.value = null
}

function formatAnnouncementTime(timestamp) {
  const date = normalizeTimestamp(timestamp)
  if (!date) return ''
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

// Initialize presence when user logs in
watch(
  () => userStore.user,
  user => {
    if (user?.uid && !isLoggingOut) {
      // Staff-only listeners. A portal customer is signed in too, and used to start
      // every one of these (walkie presence, internal chat, the customer-chat queue,
      // announcements) — reads the rules now refuse to anyone who is not staff
      // (docs/bugs/signed_in_is_not_staff_BUG.md, 2026-09-14). Return rather than
      // fall through: the else branch below is LOGOUT cleanup, and it would tear
      // down the portal's own chat listener (customerChatStore) mid-conversation.
      if (!userStore.isStaff) return

      // Initialize walkie/chat (only when chat module enabled)
      if (frontendFlags.isModuleEnabled('chat')) {
        if (!walkieStore.myUid) {
          walkieStore.initialize(
            user.uid,
            user.identity?.displayName || user.displayName || user.email || 'User',
            user.photoURL || ''
          )
        }
        if (!userChatStore.myUid) {
          userChatStore.initialize(
            user.uid,
            user.identity?.displayName || user.displayName || user.email || 'User'
          )
        }
        if (!groupChatStore.myUid) {
          groupChatStore.initialize(
            user.uid,
            user.identity?.displayName || user.displayName || user.email || 'User'
          )
        }
        if (!groupWalkieStore.myUid) groupWalkieStore.initialize(user.uid, localVoiceOwnership)

        // Customer chat: start listening for waiting/active chats immediately
        // so badge + tone work without needing to open the Chats tab first
        if (!customerChatStore.isInitialized) {
          customerChatStore.initialize()
        }
      }

      // Initialize announcement listener (runs for every STAFF user)
      if (!announcementUnsubscribe) {
        const announcementsQuery = query(
          collection(db, 'announcements'),
          orderBy('createdAt', 'desc'),
          limit(1)
        )

        let lastAnnouncementId = null
        let isFirstLoad = true

        announcementUnsubscribe = onSnapshot(announcementsQuery, snap => {
          if (!snap.empty) {
            const latest = snap.docs[0]
            const data = latest.data()

            // Skip on first load to avoid showing old announcements
            if (isFirstLoad) {
              isFirstLoad = false
              lastAnnouncementId = latest.id
              return
            }

            // Show if it's a new announcement (including sender's own for confirmation)
            if (latest.id !== lastAnnouncementId) {
              lastAnnouncementId = latest.id
              const isMine = data.senderUid === user.uid

              // Show overlay
              currentAnnouncement.value = {
                id: latest.id,
                message: data.message,
                senderName: isMine ? 'You' : data.senderName,
                createdAt: data.createdAt,
              }

              // Play sound (skip for sender — they already got a toast)
              if (!isMine) {
                playChatNotificationTone()
              }

              // Auto-dismiss after 30 seconds
              setTimeout(() => {
                if (currentAnnouncement.value?.id === latest.id) {
                  currentAnnouncement.value = null
                }
              }, 30000)
            }
          }
        })
      }
    } else {
      // User logged out - cleanup chat/walkie (module loader handles broader cleanup)
      if (frontendFlags.isModuleEnabled('chat')) {
        walkieStore.cleanup()
        userChatStore.cleanup()
        groupChatStore.cleanup()
        groupWalkieStore.cleanup()
        customerChatStore.cleanup()
      }

      // Cleanup announcement listener
      if (announcementUnsubscribe) {
        announcementUnsubscribe()
        announcementUnsubscribe = null
      }
      currentAnnouncement.value = null
    }
  },
  { immediate: true }
)

const currentGenericModal = computed(() => {
  if (modalStore.isOpen && !modalStore.isStandalone) {
    return MODAL_COMPONENT_REGISTRY[modalStore.componentKey] || null
  }
  return null
})

const currentStandaloneModal = computed(() => {
  if (modalStore.isOpen && modalStore.isStandalone) {
    return MODAL_COMPONENT_REGISTRY[modalStore.componentKey] || null
  }
  return null
})

const handleGenericModalSave = payload => {
  if (modalStore.onSave) {
    modalStore.onSave(payload)
  }
  modalStore.closeModal()
}

// Unlock audio on first user interaction
function handleFirstInteraction() {
  unlockAudio()
  // Safari (and some other browsers) require Notification.requestPermission()
  // to fire from a user gesture. initPresence() also calls this for Chrome/
  // Firefox; both are idempotent. On a fresh grant, immediately register the
  // web-push token so the call ring's out-of-tab channel arms this session
  // instead of waiting for the next app open.
  if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
    Notification.requestPermission()
      .then(perm => {
        if (perm === 'granted') {
          useWebPush()
            .syncIfGranted()
            .catch(() => {})
        }
      })
      .catch(() => {
        /* noop */
      })
  }
  document.removeEventListener('click', handleFirstInteraction)
  document.removeEventListener('keydown', handleFirstInteraction)
  document.removeEventListener('touchstart', handleFirstInteraction)
}

onMounted(() => {
  document.addEventListener('click', handleFirstInteraction)
  document.addEventListener('keydown', handleFirstInteraction)
  document.addEventListener('touchstart', handleFirstInteraction)
  console.log('Firebase App Initialized (App.vue):', firebaseApp?.name || 'No Name')
  if (analytics) {
    try {
      logEvent(analytics, 'app_mounted_screen_view', {
        firebase_screen: 'AppRoot',
        firebase_screen_class: 'AppVue',
      })
    } catch (error) {
      console.error('Error logging analytics event (App.vue):', error)
    }
  } else {
    console.log('Firebase Analytics not available or not initialized (App.vue)')
  }
})

onUnmounted(() => {
  teamVoice.stop()
  walkieStore.setTeamVoiceAdapter(null)
  document.removeEventListener('click', handleFirstInteraction)
  document.removeEventListener('keydown', handleFirstInteraction)
  document.removeEventListener('touchstart', handleFirstInteraction)

  // Clean up any pending data fetch
  if (dataFetchAbortController) {
    dataFetchAbortController.abort()
    dataFetchAbortController = null
  }
})

function shutdownInboundVoice() {
  try {
    voiceCallService.hangup?.()
  } catch (e) {
    // Ignore errors during shutdown
  }
  // Hanging up is not enough: the Device used to survive sign-out and kept
  // refreshing tokens for whoever signed in next on this tab, a portal customer
  // included (docs/bugs/voice_device_survives_sign_out_BUG.md). destroyDevice()
  // waits on its own if another tab is hosting an active call.
  try {
    voiceCallService.destroyDevice?.()
  } catch (e) {
    // Ignore errors during shutdown
  }
}

/**
 * Cleanly unsubscribe from all Firestore listeners.
 * This MUST be called BEFORE auth is revoked to prevent permission errors.
 */
function cleanupAllSubscriptions() {
  console.log('[App.vue] Cleaning up all subscriptions...')

  // Clean up all module-owned stores (notifications, comms, chat, finance, leadManagement)
  try {
    cleanupModules()
  } catch (e) {
    console.warn('[App.vue] cleanupModules error:', e)
  }

  // Clean up voice service (App-level concern)
  try {
    shutdownInboundVoice()
  } catch (e) {
    console.warn('[App.vue] voice cleanup error:', e)
  }

  // Clean up core stores
  try {
    permissionsStore.stop?.()
  } catch (e) {
    console.warn('[App.vue] permissionsStore cleanup error:', e)
  }

  // Phase 33B: Unsubscribe workflow onSnapshot listeners
  try {
    workflowStagesStore.unsubscribeAll()
  } catch (e) {
    console.warn('[App.vue] workflowStagesStore cleanup error:', e)
  }
  try {
    workflowTasksStore.unsubscribeAll()
  } catch (e) {
    console.warn('[App.vue] workflowTasksStore cleanup error:', e)
  }
  try {
    workflowProjectTypesStore.unsubscribeAll()
  } catch (e) {
    console.warn('[App.vue] workflowProjectTypesStore cleanup error:', e)
  }
  try {
    catalogCompatibilityStore.unsubscribeAll()
  } catch (e) {
    console.warn('[App.vue] catalogCompatibilityStore cleanup error:', e)
  }

  // Frontend flags are deliberately NOT unsubscribed. The document is publicly readable, and this cleanup
  // also runs on every signed-out page load (the user watcher treats "no user" as a logout). Tearing the
  // subscription down there, before its first snapshot, left every signed-out visitor on the store's
  // defaults: the Kiosk's Google map never mounted (docs/bugs/public_visitors_never_load_frontend_flags_BUG.md).
  try {
    consumerSiteConfig.unsubscribe()
  } catch (e) {
    console.warn('[App.vue] consumerSiteConfig cleanup error:', e)
  }
  try {
    blogConfig.unsubscribe()
  } catch (e) {
    console.warn('[App.vue] blogConfig cleanup error:', e)
  }
  try {
    authConfig.unsubscribeAll()
  } catch (e) {
    console.warn('[App.vue] authConfig cleanup error:', e)
  }

  // Abort any in-flight data fetches
  if (dataFetchAbortController) {
    dataFetchAbortController.abort()
    dataFetchAbortController = null
  }

  console.log('[App.vue] All subscriptions cleaned up')
}

// Expose cleanup function globally so userStore can call it before signing out
window.__appCleanupSubscriptions = cleanupAllSubscriptions

// Phase 48: Voice Device is only used for outbound connect() (dialer + conference join).
// Inbound calls use Firestore awareness — no passive push reception.
//
// Cross-tab coordination: Twilio only allows one Device registration per
// identity. If another tab on this domain is already hosting an active call,
// re-registering here would terminate that registration and drop the call.
// We defer initDevice until the other tab's call ends. The deferred init
// retries automatically when the external owner clears (see watcher below).
let pendingVoiceInit = false

// A Device belongs to the signed-in STAFF session that created it: the service
// refuses to refresh, re-register or wake-recover it for anyone else (sign-out,
// a portal customer, or a different staff member on the same tab).
voiceCallService.setSessionKeyProvider(() =>
  userStore.isStaff ? userStore.user?.uid || null : null
)

async function initializeVoiceForInbound() {
  // Honor explicit "open in new tab during active call" handoff: the spawning
  // tab appends ?noVoice=1 to keep its own Twilio Device registration alive.
  if (route.query?.noVoice === '1') {
    console.log('[App] noVoice=1 in URL — skipping Voice Device init (call handoff tab)')
    pendingVoiceInit = true
    return
  }

  if (hasActiveCallElsewhere()) {
    console.log('[App] Another tab is hosting an active call — deferring Voice Device init')
    pendingVoiceInit = true
    return
  }
  pendingVoiceInit = false

  try {
    const token = await fetchVoiceToken()
    if (!token) return

    console.log('[App] Initializing Voice Device for:', userStore.user.email)

    await voiceCallService.initDevice(token, {
      onDeviceError: err => console.error('Twilio Device Error:', err),
      // Keeps the device alive past the access token's TTL (proactive
      // tokenWillExpire refresh + fresh-token re-registration) — without it an
      // idle tab's device dies on token expiry and Answer hard-refuses.
      fetchToken: fetchVoiceToken,
      // No onIncoming — inbound calls use conference join, not passive push
    })
  } catch (e) {
    console.error('Failed to init inbound voice:', e)
  }
}

async function fetchVoiceToken() {
  const functionsBase =
    process.env.VUE_APP_FUNCTIONS_BASE ||
    `https://us-central1-${functions.app?.options?.projectId || 'gse-main-site'}.cloudfunctions.net`
  const url = `${functionsBase}/twilioUnifiedFunction`
  const identity = (userStore.user?.email || '').toLowerCase()
  if (!identity) return null
  // Voice is staff-only. A stale reference to this provider must never mint a
  // calling token for a non-staff session.
  if (!userStore.isStaff) return null

  console.log('[App] Requesting Voice Token for:', identity)

  const resp = await fetch(url, {
    method: 'POST',
    headers: await jsonAuthHeaders(),
    body: JSON.stringify({ action: 'generateToken', identity }),
  })

  // A refused request is an answer, not a failure: this session may not hold a
  // calling token (a role the server refuses, or a test walk once the agent
  // outbound fence enforces). No token, no device, and no JSON parse of the
  // plain-text error body. Other failures still throw, as before.
  if (resp.status === 401 || resp.status === 403) {
    console.log(`[App] Calling token not issued for this session (${resp.status})`)
    return null
  }

  const data = await resp.json()
  return data.token || null
}

// Wake-path recovery: a tab returning from sleep/offline may hold a device
// that exhausted its re-register backoff or sits on an expired token.
// ensureRegistered() no-ops when already registered or a peer tab owns the
// registration.
function recoverVoiceOnWake() {
  if (typeof document !== 'undefined' && document.hidden) return
  if (!userStore.user?.uid || !userStore.isStaff) return
  if (!frontendFlags.isModuleEnabled('communications')) return
  voiceCallService.ensureRegistered()
}
document.addEventListener('visibilitychange', recoverVoiceOnWake)
window.addEventListener('online', recoverVoiceOnWake)

// When the call-hosting tab finishes (or its heartbeat goes stale), retry our
// own Voice Device init if we deferred earlier.
onExternalOwnerChange(owner => {
  if (
    !owner &&
    pendingVoiceInit &&
    userStore.user?.uid &&
    userStore.isStaff &&
    frontendFlags.isModuleEnabled('communications')
  ) {
    console.log('[App] External call ended — retrying Voice Device init')
    initializeVoiceForInbound()
  }
})

// Request browser notification permission for incoming call alerts
if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
  Notification.requestPermission().catch(() => {})
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN USER STATE WATCHER - Handles login/logout transitions
// ═══════════════════════════════════════════════════════════════════════════════
watch(
  () => userStore.user,
  async (newUser, oldUser) => {
    // Skip if auth state is still being determined
    if (!userStore.isInitialized) {
      return
    }

    const wasLoggedIn = !!oldUser
    const isNowLoggedIn = !!newUser
    const isLoggingInNow = !wasLoggedIn && isNowLoggedIn
    const isLoggingOutNow = wasLoggedIn && !isNowLoggedIn

    console.time('[PERF] [App.vue] User State Change Logic')

    // ═══════════════════════════════════════════════════════════════════════════
    // LOGOUT HANDLING - Clean up FIRST to prevent permission errors
    // ═══════════════════════════════════════════════════════════════════════════
    if (isLoggingOutNow || !newUser) {
      console.log('[App.vue] User logged out, cleaning up...')
      isLoggingOut = true

      // Cancel any in-flight data fetches
      if (dataFetchAbortController) {
        dataFetchAbortController.abort()
        dataFetchAbortController = null
      }

      // Clean up all subscriptions (this prevents permission errors)
      cleanupAllSubscriptions()

      // Stop loading spinner if it was showing
      eventBus.emit('loading-stop')

      // ✅ If we're on a protected route, navigate to signed-out page
      const currentRoute = route.name
      const isOnPublicRoute = [
        'ConsumerHome',
        'SignIn',
        'SignedOut',
        'PrivacyPolicy',
        'TermsOfService',
        'ConsumerPrivacyPolicy',
        'ConsumerTermsOfService',
        'AboutUs',
        'ContactUs',
        'CompleteInvitation',
        'Maintenance',
      ].includes(currentRoute)

      // A signed-in session ended without this tab's signOutUser (another tab signed out, the account was revoked):
      // close every listener it opened and load the page fresh, so no store keeps its listeners or data
      // (src/services/sessionEnd.js). Not on a signed-out visitor's page load (no session ended), and not while
      // signOutUser is ending the session itself: it loads the fresh page once it has signed out.
      if (planSessionTransition(oldUser, newUser) === 'ended' && !isSessionEnding()) {
        console.log('[App.vue] Session ended elsewhere, loading a fresh page...')
        reloadAfterSessionChange({ transition: 'ended', onPublicRoute: isOnPublicRoute })
      }

      isLoggingOut = false
      console.timeEnd('[PERF] [App.vue] User State Change Logic')
      return
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // LOGIN HANDLING
    // ═══════════════════════════════════════════════════════════════════════════
    console.log('[App.vue] User authenticated, initializing app...')

    // A different account signed in without passing through "no user" (the
    // logout branch above never ran): nothing of the previous session may carry
    // over into this one: not its voice device, its listeners, or any store's
    // data. Tear the device down, then load the page fresh for the new account
    // (src/services/sessionEnd.js), as the impersonation switch does.
    if (planSessionTransition(oldUser, newUser) === 'switched') {
      console.log('[App.vue] Account switched — tearing down the previous session')
      pendingVoiceInit = false
      shutdownInboundVoice()
      reloadAfterSessionChange({ transition: 'switched', onPublicRoute: false })
      console.timeEnd('[PERF] [App.vue] User State Change Logic')
      return
    }

    // Show loading spinner for new logins
    if (isLoggingInNow) {
      console.log('[App.vue] New login detected, showing loading state...')
      eventBus.emit('loading-start')
    }

    // Create abort controller for this fetch cycle
    dataFetchAbortController = new AbortController()
    const signal = dataFetchAbortController.signal

    try {
      // Post-sign-in redirect.
      //
      // Why a sessionStorage flag and not just `isLoggingInNow`: the watcher
      // can't distinguish "user just clicked Sign In" from "page reloaded with
      // a signed-in session restored from cookies" — both produce
      // `oldUser=null, newUser=userObj` on the first post-init fire. Without a
      // flag, every page reload of a signed-in user would redirect them away
      // from wherever they were.
      //
      // The flag (`userInitiatedSignIn`) is set in the sign-in entry points
      // (SignInPage, CustomerSignInPage, CustomerActivatePage, ConsumerLoginOverlay) before password auth
      // and is consumed here, after the CRM user profile is ready.
      //
      // If a router guard bounced the user to /sign-in from a protected route
      // (`?redirect=…`), honor that target. Open-redirect protection: only
      // accept relative paths starting with a single `/`.
      if (isLoggingInNow && sessionStorage.getItem('userInitiatedSignIn') === '1') {
        sessionStorage.removeItem('userInitiatedSignIn')

        const userRoles = userStore.roles
        const primaryRole = userRoles.includes('admin') ? 'admin' : userRoles[0] || 'sales'

        const raw = route.query.redirect
        const isSafeRedirect =
          typeof raw === 'string' && raw.startsWith('/') && !raw.startsWith('//')
        // Phones (staff, not customers) land on the mobile sidecar unless the
        // user has opted into the full site. An explicit ?redirect= still wins.
        const useMobile = shouldUseMobileSidecar() && !userStore.isCustomer
        // Activation has always opened the portal for customers and Studio for
        // staff. Choose that destination here, after profile hydration.
        const activationPath =
          route.name === 'CustomerActivate' ? (userStore.isCustomer ? '/portal' : '/studio') : null
        const path =
          activationPath ||
          (isSafeRedirect ? raw : useMobile ? '/m' : handleUserRoleRedirect(primaryRole))

        console.log(`[App.vue] Post-sign-in redirect (role: ${primaryRole}) → ${path}`)

        // Don't await - let navigation happen asynchronously
        router.replace(path).catch(err => {
          // Ignore navigation duplicated errors
          if (err.name !== 'NavigationDuplicated') {
            console.warn('[App.vue] Navigation error:', err)
          }
        })
      } else if (route.name === 'SignedOut') {
        // This tab was left on the signed-out page and the session came back to it: auth is shared by every
        // tab, so signing in again in another tab signs this one in too, and everything below starts here.
        // The page must not keep saying "You've been signed out" over a live session (its chat listeners,
        // its push toasts), so the tab goes where the router sends a signed-in user
        // (docs/bugs/signed_out_page_hosts_live_session_BUG.md).
        console.log('[App.vue] Session is live on the signed-out page, leaving it')
        router.replace(homePathForRoles(userStore.roles)).catch(err => {
          if (err.name !== 'NavigationDuplicated') {
            console.warn('[App.vue] Navigation error:', err)
          }
        })
      }

      // Check if we were aborted (user logged out during initialization)
      if (signal.aborted) {
        console.log('[App.vue] Data fetch aborted (user logged out)')
        return
      }

      const roles = userStore.roles

      // External platform reviewers are deliberately not staff. Their only
      // authenticated surface is the isolated Marketing Hub review workspace,
      // which owns its own synthetic-data callable. Do not initialize the
      // access matrix or any internal store/listener before returning here.
      if (userStore.isMarketingReviewer) {
        console.log('[App.vue] Marketing reviewer session: internal app initialization skipped')
        return
      }

      // TIER 1: Core permissions (always loaded first)
      await permissionsStore.init()

      // Everything below is the STAFF app: alert preferences, module stores, leads,
      // the Twilio voice device (which mints a calling token) and the customer and
      // user directories. A portal customer needs only the permissions above — the
      // portal loads its own data (usePortalData). Before 2026-09-14 portal sessions
      // ran all of it, including minting a voice token on every sign-in.
      if (!userStore.isStaff) {
        console.log('[App.vue] Non-staff session: staff initialization skipped')
        return
      }

      alertPreferencesStore.load?.()

      // TIER 2: Module stores (conditionally loaded based on feature flags)
      await initializeModules({ roles, userStore })

      // TIER 3: Core data fetches (leadStore is core — dashboard depends on it)
      leadStore.fetchLeads()

      // Voice initialization (communications module concern, but needs App-level
      // wiring). Every signed-in user needs a Twilio Device so they can join a
      // conference when invited via Invite-to-Call — the Device is just a token
      // registration and costs nothing until actual call minutes are used.
      // Dialer / inbound-answer gating stays role-scoped inside CallPanel.
      if (frontendFlags.isModuleEnabled('communications')) {
        initializeVoiceForInbound()
        // Arm the out-of-tab ring channel on DESKTOP too: silently register a
        // web-push token when notification permission is already granted (no
        // new prompt). onActiveCallSessionRinging already fans out web pushes
        // per ring — until now only mobile ever registered a platform:'web'
        // token, so desktop had a single in-tab wake channel and a throttled/
        // frozen tab meant a silently missed call.
        useWebPush()
          .syncIfGranted()
          .catch(() => {})
      } else {
        shutdownInboundVoice()
      }

      // Check abort again before starting heavy fetches
      if (signal.aborted) {
        console.log('[App.vue] Data fetch aborted before heavy fetches')
        return
      }

      // TIER 4: Core parallel fetches (module stores already fetched by initializeModules)
      // Phase 33B: workflow stores use onSnapshot subscriptions (instant from IndexedDB cache)
      workflowStagesStore.subscribe()
      workflowTasksStore.subscribe()
      workflowProjectTypesStore.subscribe()
      // The configured test names behind every isTestCustomer (Settings → Test Customers): staff read, live.
      testCustomerMarkersStore.subscribe()
      const fetchPromises = [dataStore.fetchCustomers()]

      if (!roles.includes('setter') || roles.includes('admin')) {
        fetchPromises.push(usersStore.fetchAllUsers())
      }

      // Race against timeout to prevent infinite hang (30 second max)
      const FETCH_TIMEOUT_MS = 30000

      await Promise.race([
        Promise.allSettled(fetchPromises),
        new Promise(resolve => setTimeout(resolve, FETCH_TIMEOUT_MS)),
      ])

      console.log('[App.vue] Initial data fetch complete')
    } catch (err) {
      // Don't log abort errors - they're expected when user logs out during fetch
      if (err.name === 'AbortError' || signal?.aborted) {
        console.log('[App.vue] Data fetch aborted (user logged out during fetch)')
        return
      }

      // Log but don't block - user can still use the app, data will load lazily
      console.error('[App.vue] Error during app initialization:', err)

      // If it was a timeout, log it specifically
      if (err.message === 'Data fetch timeout') {
        console.warn('[App.vue] Data fetch timed out after 30 seconds - continuing anyway')
      }
    } finally {
      // ALWAYS stop the loading spinner, no matter what happened
      eventBus.emit('loading-stop')
      dataFetchAbortController = null
      console.timeEnd('[PERF] [App.vue] User State Change Logic')
    }
  },
  { immediate: true }
)
</script>

<style>
.announcement-enter-active {
  animation: bounceIn 0.5s ease-out;
}

.announcement-leave-active {
  animation: fadeOut 0.3s ease-in;
}

@keyframes bounceIn {
  0% {
    opacity: 0;
    transform: scale(0.8) translateY(-20px);
  }

  50% {
    transform: scale(1.02) translateY(0);
  }

  100% {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

@keyframes fadeOut {
  from {
    opacity: 1;
  }

  to {
    opacity: 0;
    transform: translateY(-10px);
  }
}
</style>
